import React, { useState, useEffect } from 'react';
import {
  Music2, Link2, QrCode, Plug, RefreshCw, Trash2, CheckCircle2, AlertCircle, ExternalLink, Plus, Loader2,
  MessageCircle, Instagram, Facebook, Mail, Smartphone, Radio
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { UnderConstructionButton } from '../components/UnderConstruction';
import { MetaConnectionCenter } from '../components/MetaConnectionCenter';
import { Header } from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ connected, label }) => (
  <Badge
    variant={connected ? 'default' : 'outline'}
    className={connected ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30'}
    data-testid={`status-${label?.toLowerCase()}`}
  >
    {connected ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
    {connected ? 'Conectado' : 'Sin conexion'}
  </Badge>
);

const ChannelCard = ({ icon, name, color, status, action, children }) => (
  <Card className="glass-card overflow-hidden" data-testid={`channel-card-${name.toLowerCase().replace(/\s/g, '-')}`}>
    <CardHeader className="pb-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl shrink-0" style={{ background: `${color}22` }}>
            {icon}
          </div>
          <div className="min-w-0">
            <CardTitle className="text-base leading-tight break-words">{name}</CardTitle>
            <CardDescription className="text-xs break-words">{status}</CardDescription>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">{action}</div>
      </div>
    </CardHeader>
    {children && <CardContent className="pt-0">{children}</CardContent>}
  </Card>
);

const TikTokPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/tiktok/status`)
      .then(({ data }) => { if (!cancelled) setStatus(data); })
      .catch(() => { if (!cancelled) toast.error('No se pudo obtener estado TikTok'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const load = () => setReloadKey(k => k + 1);

  const connect = async () => {
    try {
      const { data } = await axios.get(`${API}/tiktok/auth/url`);
      window.open(data.auth_url, '_blank', 'width=720,height=820');
      toast.info('Autoriza TikTok en la ventana emergente y vuelve aqui.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar OAuth');
    }
  };

  if (loading) return <div className="flex items-center justify-center p-8"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <ChannelCard
      icon={<Music2 className="w-5 h-5 text-foreground" />}
      name="TikTok Business"
      color="#FE2C55"
      status={status?.configured ? (status?.connected ? `Advertiser: ${status.advertiser_id}` : 'Credenciales listas, falta vincular') : 'Falta configurar credenciales'}
      action={
        <div className="flex gap-2 items-center">
          <StatusBadge connected={status?.connected} label="TikTok" />
          <Button size="sm" variant="ghost" onClick={load} data-testid="tiktok-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      }
    >
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 mt-2">
        <Button onClick={connect} disabled={!status?.configured} className="w-full sm:w-auto bg-brand-copper hover:bg-fuchsia-700 text-white" data-testid="tiktok-connect-btn">
          <Plug className="w-4 h-4 mr-2" />{status?.connected ? 'Reconectar TikTok' : 'Conectar TikTok'}
        </Button>
        <div className="text-xs text-muted-foreground min-w-0">
          Webhook: <code className="bg-muted px-1 rounded break-all">{API}/tiktok/webhook</code>
        </div>
      </div>
    </ChannelCard>
  );
};

const WhatsAppWebPanel = () => {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/whatsapp-web/sessions`)
      .then(({ data }) => {
        if (cancelled) return;
        setSessions(data || []);
        const connected = (data || []).find(s => s.status === 'connected');
        if (connected) setActive(connected);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const load = () => setReloadKey(k => k + 1);

  // Poll QR when awaiting scan
  useEffect(() => {
    if (!active || active.status === 'connected') return undefined;
    const t = setInterval(() => {
      axios.get(`${API}/whatsapp-web/sessions/${active.id}/qr`)
        .then(({ data }) => {
          if (data.status === 'connected') {
            toast.success('WhatsApp vinculado correctamente');
            setActive(prev => prev ? { ...prev, status: 'connected', phone_number: data.phone_number } : prev);
            setReloadKey(k => k + 1);
          } else if (data.qr_code) {
            setActive(prev => prev ? { ...prev, qr_code: data.qr_code, status: data.status } : prev);
          }
        })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [active]);

  const createSession = async () => {
    try {
      const { data } = await axios.post(`${API}/whatsapp-web/sessions/create`, { session_name: `Sesion ${sessions.length + 1}` });
      if (data.status === 'already_connected') {
        toast.info('Ya hay una sesion conectada');
        load();
        return;
      }
      toast.success('Sesion creada. Esperando QR del puente WhatsApp...');
      setActive({ id: data.session_id, status: 'pending_qr' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear sesion');
    }
  };

  const disconnect = async (id) => {
    try {
      await axios.delete(`${API}/whatsapp-web/sessions/${id}`);
      toast.success('Sesion desconectada');
      if (active?.id === id) setActive(null);
      load();
    } catch (e) {
      toast.error('No se pudo desconectar');
    }
  };

  if (loading) return <div className="flex items-center justify-center p-8"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const connected = sessions.find(s => s.status === 'connected');

  return (
    <ChannelCard
      icon={<span>💬</span>}
      name="WhatsApp Web (QR)"
      color="#25D366"
      status={connected ? `Vinculado: ${connected.phone_number || 'numero oculto'}` : 'Escanea QR para vincular tu numero'}
      action={
        <div className="flex gap-2 items-center">
          <StatusBadge connected={!!connected} label="WhatsApp" />
          <Button size="sm" variant="ghost" onClick={load} data-testid="wa-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      }
    >
      <div className="space-y-4 mt-2">
        {!connected && (
          <UnderConstructionButton
            label="Vincular número por QR"
            detail="El puente de WhatsApp Web requiere un servicio dedicado que aún no está encendido en este servidor. Usa WhatsApp Cloud por campaña mientras tanto."
            testid="wa-connect-btn"
            icon={QrCode}
            className="w-full sm:w-auto"
          />
        )}

        {active && active.status !== 'connected' && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex flex-col sm:flex-row gap-4 items-start">
            <div className="bg-white p-3 rounded-xl flex-shrink-0">
              {active.qr_code ? (
                <QRCodeSVG value={active.qr_code} size={192} level="M" />
              ) : (
                <div className="w-48 h-48 flex flex-col items-center justify-center text-zinc-500 text-xs text-center px-4 bg-zinc-100 rounded">
                  <Loader2 className="w-6 h-6 animate-spin mb-2" />
                  Esperando QR del puente...
                  <span className="mt-1 text-[10px]">Si tarda mas de 30s, configura el WhatsApp Bridge service</span>
                </div>
              )}
            </div>
            <div className="flex-1 text-sm space-y-2">
              <p className="font-medium">Para vincular tu WhatsApp:</p>
              <ol className="list-decimal list-inside text-muted-foreground text-xs space-y-1">
                <li>Abre WhatsApp en tu telefono</li>
                <li>Menu &gt; Dispositivos vinculados</li>
                <li>Vincular un dispositivo</li>
                <li>Escanea este codigo</li>
              </ol>
              {active && active.status !== 'connected' && <p className="text-xs text-emerald-700 dark:text-emerald-400">⟳ Esperando escaneo...</p>}
              <p className="text-[11px] text-muted-foreground">
                Bridge webhook: <code className="bg-muted px-1 rounded">{API}/whatsapp-web/bridge/webhook</code>
              </p>
            </div>
          </div>
        )}

        {sessions.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium">Sesiones</p>
            {sessions.map(s => (
              <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/40" data-testid={`wa-session-${s.id}`}>
                <div className="text-sm">
                  <span className="font-medium">{s.session_name}</span>
                  <span className="text-muted-foreground ml-2 text-xs">{s.phone_number || s.status}</span>
                </div>
                <div className="flex gap-1 items-center">
                  <StatusBadge connected={s.status === 'connected'} label={`wa-${s.id}`} />
                  <Button size="icon" variant="ghost" onClick={() => disconnect(s.id)} data-testid={`wa-disconnect-${s.id}`}>
                    <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ChannelCard>
  );
};

const TwilioPanel = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    axios.get(`${API}/twilio/status`).then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, []);
  return (
    <ChannelCard
      icon={<span>✉️</span>}
      name="Twilio SMS"
      color="#F22F46"
      status={status?.configured ? `Activo desde ${status.phone_number}` : 'Esperando credenciales (SID, Auth Token, Phone)'}
      action={<StatusBadge connected={status?.configured} label="Twilio" />}
    >
      {!status?.configured && (
        <div className="text-xs text-muted-foreground mt-2 space-y-2">
          <UnderConstructionButton
            label="Asistente guiado de Twilio"
            detail="Crearemos la subcuenta y el número por ti, sin copiar SID ni tokens. Mientras tanto, configura SMS por campaña en Campañas → botón SMS."
            testid="twilio-wizard-btn"
            className="w-full sm:w-auto"
          />
          <p>Hoy puedes activar SMS pegando tus credenciales por campaña (Campañas → botón <b>SMS</b>).</p>
          <code className="block bg-muted p-2 rounded text-[10px] break-all">
            TWILIO_ACCOUNT_SID=ACxxxxx<br/>
            TWILIO_AUTH_TOKEN=xxxxx<br/>
            TWILIO_PHONE_NUMBER=+1xxxxxx
          </code>
          <Button asChild variant="outline" size="sm" className="w-full sm:w-auto" data-testid="twilio-console-link">
            <a href="https://console.twilio.com/" target="_blank" rel="noreferrer">
              Abrir consola de Twilio <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </a>
          </Button>
        </div>
      )}
    </ChannelCard>
  );
};

const OVCPartnersPanel = () => {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({ app: 'meow', webhook_url: '', secret_key: '' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/ovc/partners`)
      .then(({ data }) => { if (!cancelled) setPartners(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [reloadKey]);

  const register = async () => {
    if (!form.webhook_url) { toast.error('URL del webhook es requerida'); return; }
    try {
      await axios.post(`${API}/ovc/partners`, form);
      toast.success(`Partner ${form.app} registrado`);
      setForm({ app: 'meow', webhook_url: '', secret_key: '' });
      setReloadKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al registrar');
    }
  };

  const remove = async (app) => {
    try {
      await axios.delete(`${API}/ovc/partners/${app}`);
      toast.success('Partner eliminado');
      setReloadKey(k => k + 1);
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <Card className="glass-card" data-testid="ovc-partners-panel">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4" /> OVC Universe Partners</CardTitle>
        <CardDescription>Registra apps externas (Meow, Octopus, WorkOps, custom) para recibir broadcasts de DEAL</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select
            value={form.app}
            onChange={e => setForm({ ...form, app: e.target.value })}
            className="px-3 py-2 rounded-md border bg-background text-sm"
            data-testid="ovc-app-select"
          >
            <option value="meow">Meow</option>
            <option value="octopus">Octopus</option>
            <option value="workops">WorkOps</option>
            <option value="bruno">Bruno</option>
            <option value="custom">Custom</option>
          </select>
          <Input
            placeholder="https://app.example.com/webhook"
            value={form.webhook_url}
            onChange={e => setForm({ ...form, webhook_url: e.target.value })}
            data-testid="ovc-webhook-input"
          />
          <Input
            placeholder="Secret key (HMAC, opcional)"
            value={form.secret_key}
            onChange={e => setForm({ ...form, secret_key: e.target.value })}
            data-testid="ovc-secret-input"
          />
          <Button onClick={register} className="bg-brand-green hover:bg-brand-green text-white" data-testid="ovc-register-btn">
            <Plus className="w-4 h-4 mr-2" />Registrar
          </Button>
        </div>

        <div className="space-y-2">
          {partners.length === 0 && <p className="text-xs text-muted-foreground">Sin partners registrados aun.</p>}
          {partners.map(p => (
            <div key={p.app} className="flex items-center justify-between p-2 rounded-lg bg-muted/40" data-testid={`ovc-partner-${p.app}`}>
              <div className="text-sm">
                <span className="font-medium">{p.name}</span>
                <span className="text-muted-foreground ml-2 text-xs">{p.webhook_url}</span>
                {p.secret_key && <Badge variant="outline" className="ml-2 text-[10px]">HMAC</Badge>}
              </div>
              <Button size="icon" variant="ghost" onClick={() => remove(p.app)} data-testid={`ovc-remove-${p.app}`}>
                <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
              </Button>
            </div>
          ))}
        </div>

        <div className="text-[11px] text-muted-foreground space-y-1">
          <p>📡 Para recibir eventos desde otras apps, expon el endpoint:</p>
          <code className="block bg-muted p-2 rounded">POST {API}/ovc/inbound/{'{app}'}</code>
          <p>Firma con header <code className="bg-muted px-1 rounded">X-OVC-Signature</code> = HMAC-SHA256(secret, body)</p>
        </div>
      </CardContent>
    </Card>
  );
};

const BrunoPanel = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    axios.get(`${API}/bruno/stats`).then(({ data }) => setStatus({ ok: true, ...data })).catch(() => setStatus({ ok: false }));
  }, []);
  return (
    <ChannelCard
      icon={<span>🐶</span>}
      name="BRUNO.APP"
      color="#FF6B35"
      status={status?.ok ? `Webhooks activos${status?.total_deals !== undefined ? ` · ${status.total_deals} deals` : ''}` : 'No configurado'}
      action={<StatusBadge connected={status?.ok} label="Bruno" />}
    >
      <a href="/bruno" className="text-xs text-primary inline-flex items-center gap-1 mt-2" data-testid="bruno-panel-link">
        Abrir panel Bruno <ExternalLink className="w-3 h-3" />
      </a>
    </ChannelCard>
  );
};

const CHANNEL_META = {
  whatsapp:     { label: 'WhatsApp Cloud',   Icon: MessageCircle, color: '#25D366' },
  whatsapp_web: { label: 'WhatsApp Web',     Icon: QrCode,        color: '#128C7E' },
  messenger:    { label: 'Messenger',        Icon: Facebook,      color: '#0084FF' },
  instagram:    { label: 'Instagram',        Icon: Instagram,     color: '#E1306C' },
  sms:          { label: 'SMS (Twilio)',     Icon: Smartphone,    color: '#F22F46' },
  email:        { label: 'Email',            Icon: Mail,          color: '#3B82F6' },
};

const STATUS_STYLE = {
  production:     'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  sandbox:        'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
  development:    'bg-brand-green/15 text-brand-deep dark:text-brand-green border-brand-green/30',
  not_configured: 'bg-muted text-foreground/75 border-border',
};

const STATUS_LABEL = {
  production: 'Producción',
  sandbox: 'Sandbox',
  development: 'Desarrollo',
  not_configured: 'No configurado',
};

function ChannelStatusGrid() {
  const [data, setData] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/channels/status`);
      setData(res.data);
    } catch (e) {
      toast.error('No se pudo obtener estado de canales');
    }
  };

  useEffect(() => { load(); }, []);

  const handleTest = async (platform) => {
    setTesting(platform);
    try {
      const res = await axios.post(`${API}/channels/${platform}/test`, {});
      if (res.data?.configured) toast.success(`${platform}: ${res.data.message}`);
      else toast.warning(`${platform}: ${res.data.message}`);
    } catch {
      toast.error(`Error al testear ${platform}`);
    } finally {
      setTesting(null);
    }
  };

  const providers = data?.providers || {};

  return (
    <Card className="glass-card" data-testid="channels-status-grid">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Radio className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
              Estado de canales
            </CardTitle>
            <CardDescription className="text-xs">Vista unificada de tus proveedores de mensajería</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="channels-reload-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {Object.entries(providers).map(([platform, info]) => {
            const meta = CHANNEL_META[platform] || { label: platform, Icon: Plug, color: '#888' };
            const { Icon } = meta;
            const status = info.status;
            return (
              <div
                key={platform}
                data-testid={`channel-status-${platform}`}
                className="p-3 rounded-xl border border-border/50 bg-muted/20 hover:bg-muted/30 transition"
              >
                <div className="flex items-center gap-2.5 mb-2.5">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}22` }}>
                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-tight">{meta.label}</p>
                    <Badge className={`text-[10px] h-auto py-0.5 px-1.5 mt-1 inline-block ${STATUS_STYLE[status] || STATUS_STYLE.not_configured}`}>
                      {STATUS_LABEL[status] || status}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-8 text-xs"
                  onClick={() => handleTest(platform)}
                  disabled={testing === platform}
                  data-testid={`channel-test-${platform}`}
                >
                  {testing === platform ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Probar conexión'}
                </Button>
              </div>
            );
          })}
          {!data && <div className="col-span-full text-center text-sm text-muted-foreground py-6">Cargando…</div>}
        </div>
      </CardContent>
    </Card>
  );
}


export default function SocialConnections() {
  return (
    <div data-testid="social-connections-page" className="min-h-screen pb-24">
      <Header title="Conexiones Sociales" subtitle="Vincula tus redes y servicios para sincronizar chats omnicanal" />
      <div className="container mx-auto px-4 py-6 max-w-5xl space-y-4">

        {/* Hero explicativo */}
        <Card className="glass-card border-violet-500/30 bg-primary/10" data-testid="connections-hero">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold flex items-center gap-2 mb-2">
              <span className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-sm">✨</span>
              Cómo funciona esto
            </h3>
            <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal list-inside">
              <li><b>Conecta</b> tus cuentas (Meta, TikTok, WhatsApp, SMS Twilio) con los botones de abajo.</li>
              <li><b>Noah atiende</b> los mensajes entrantes 24/7 con tu estilo (Voice Cloning) — ventas o servicio según la campaña.</li>
              <li><b>Noah recolecta</b> automáticamente: teléfono, email, presupuesto, objeciones, sentimiento, urgencia, intención de compra.</li>
              <li><b>Revisa y cierra</b> en <a href="/deals" className="text-brand-deep dark:text-brand-deep dark:text-brand-green font-semibold underline">Gestión Noah →</a> — todos los datos listos para finalizar la venta.</li>
            </ol>
          </CardContent>
        </Card>

        <ChannelStatusGrid />

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-4" data-testid="connections-tabs">
            <TabsTrigger value="all" data-testid="tab-all">Todas</TabsTrigger>
            <TabsTrigger value="messaging" data-testid="tab-messaging">Mensajeria</TabsTrigger>
            <TabsTrigger value="ovc" data-testid="tab-ovc">OVC Universe</TabsTrigger>
          </TabsList>
          <TabsContent value="all" className="space-y-4">
            <MetaConnectionCenter />
            <TikTokPanel />
            <WhatsAppWebPanel />
            <TwilioPanel />
            <BrunoPanel />
          </TabsContent>
          <TabsContent value="messaging" className="space-y-4">
            <MetaConnectionCenter />
            <TikTokPanel />
            <WhatsAppWebPanel />
            <TwilioPanel />
          </TabsContent>
          <TabsContent value="ovc" className="space-y-4">
            <BrunoPanel />
            <OVCPartnersPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
